/**
 * Pure fit-bucket helpers for the AI Recruiter Console.
 *
 * Defines the canonical RED/ORANGE/GREEN bucket scheme that unifies the
 * ConsoleFitBadge thresholds (green≥8 / amber 5-7 / red≤4) with the
 * filter UI and the server-side score IN-clause.  Server-side mapping is
 * the single source of truth for the score ranges; this file is the
 * single source of truth on the FRONTEND for labels and validation.
 *
 * Extracted for testability — no React/Apollo imports (mirrors selection.util.ts).
 */

export type FitBucket = 'RED' | 'ORANGE' | 'GREEN';

export interface FitBucketOption {
  value: FitBucket;
  label: string;
  /** Inclusive [min, max] integer range. */
  range: [number, number];
}

/**
 * Ordered high→low so the dropdown reads Green / Orange / Red.
 * Labels include the range so recruiters can see the cutoffs at a glance
 * (decision #2: "Red (1-4)", "Orange (5-7)", "Green (8-10)").
 */
export const FIT_BUCKET_OPTIONS: readonly FitBucketOption[] = [
  { value: 'GREEN', label: 'Green (8-10)', range: [8, 10] },
  { value: 'ORANGE', label: 'Orange (5-7)', range: [5, 7] },
  { value: 'RED', label: 'Red (1-4)', range: [1, 4] },
];

/**
 * Type-guard: true only for the three valid bucket strings.
 * Used by the URL parser to drop unrecognised ?fit= tokens.
 */
export const isFitBucket = (v: string): v is FitBucket =>
  v === 'RED' || v === 'ORANGE' || v === 'GREEN';

/**
 * Return the display label for a known FitBucket.
 * Falls back to the raw value for unknown inputs so callers never crash.
 */
export const fitBucketLabel = (v: FitBucket): string =>
  FIT_BUCKET_OPTIONS.find((o) => o.value === v)?.label ?? v;
